import { AuthBase } from "./AuthBase";
/**
 * Auth event types
 */
export var AuthEventType;
(function (AuthEventType) {
    AuthEventType["LOGIN_REQUIRED"] = "login_required";
    AuthEventType["LOGIN_SUCCESS"] = "login_success";
    AuthEventType["LOGIN_FAILURE"] = "login_failure";
    AuthEventType["LOGOUT"] = "logout";
})(AuthEventType || (AuthEventType = {}));
/**
 * Browser-specific implementation of authentication service
 */
export class AuthBrowser extends AuthBase {
    eventListeners = [];
    /**
     * Create a new browser authentication service
     * @param config - Configuration options
     * @param fetchImpl - The fetch implementation to use
     */
    constructor(config, fetchImpl = window.fetch.bind(window)) {
        super(config, fetchImpl);
    }
    /**
     * Add an event listener
     * @param listener - The event listener to add
     */
    addEventListener(listener) {
        this.eventListeners.push(listener);
    }
    /**
     * Remove an event listener
     * @param listener - The event listener to remove
     */
    removeEventListener(listener) {
        const index = this.eventListeners.indexOf(listener);
        if (index !== -1) {
            this.eventListeners.splice(index, 1);
        }
    }
    /**
     * Emit an event to all listeners
     * @param event - The event type
     * @param data - Optional event data
     */
    emitEvent(event, data) {
        this.eventListeners.forEach((listener) => {
            try {
                listener(event, data);
            }
            catch (error) {
                console.error("Error in auth event listener:", error);
            }
        });
    }
    /**
     * Get the access token from session storage
     * @returns Promise with the access token
     */
    async getAccessToken() {
        return this.getRawToken();
    }
    /**
     * Get the raw token from session storage
     * @returns The raw token
     */
    getRawToken() {
        if (typeof window === "undefined" || !window.localStorage) {
            return null;
        }
        return localStorage.getItem("accessToken");
    }
    /**
     * Perform auto sign-in using environment variables
     *
     * Required environment variables:
     * - VUE_APP_CSA_USERNAME: The username for auto sign-in
     * - VUE_APP_CSA_PASSWORD: The password for auto sign-in
     *
     * @returns Promise that resolves when auto sign-in is complete
     */
    async autoSignIn() {
        // If we already have a valid token, no need to sign in again
        if (this.getRawToken()) {
            return;
        }
        // Check if environment variables are available
        if (!process.env.VUE_APP_CSA_USERNAME ||
            !process.env.VUE_APP_CSA_PASSWORD) {
            // Emit login required event
            this.emitEvent(AuthEventType.LOGIN_REQUIRED);
            return;
        }
        await this.signIn(process.env.VUE_APP_CSA_USERNAME, process.env.VUE_APP_CSA_PASSWORD);
    }
    /**
     * Sign in with username and password
     * @param username - The username
     * @param password - The password
     * @returns Promise with the authentication response
     */
    async signIn(username, password) {
        const response = await this.authenticate(username, password);
        if (response && response.access_token) {
            // Store the access token
            localStorage.setItem("accessToken", response.access_token);
            // Store the user ID
            const userId = this.getUserId();
            if (!userId) {
                // If user ID is not available, throw an error
                const error = new Error("User ID not found in access token");
                this.emitEvent(AuthEventType.LOGIN_FAILURE, { error });
                throw error;
            }
            // Store the user ID in session storage
            localStorage.setItem("userId", userId);
            this.emitEvent(AuthEventType.LOGIN_SUCCESS, { response });
        }
        else {
            this.emitEvent(AuthEventType.LOGIN_FAILURE, { response });
        }
        return response;
    }
    /**
     * Sign out
     */
    signOut() {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userId");
        this.emitEvent(AuthEventType.LOGOUT);
    }
}
